'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuWifi, LuWifiOff, LuTriangleAlert, LuRefreshCw } from 'react-icons/lu';

import { useNetworkStatus } from '../hooks/use-network-status';

// RULE 15: Direct imports to prevent circular dependency with index-chakra
import { Box } from './box';
import { HStack } from './stack';
import { Text } from './text';
import { Button } from './button';
import { IconButton } from './icon-button';

export interface NetworkStatusBannerProps {
  /**
   * Show banner even when connection is good
   * @default false
   */
  showWhenOnline?: boolean;

  /**
   * Auto-hide delay in ms when connection is restored
   * @default 3000
   */
  autoHideDelay?: number;

  /**
   * Show connection quality indicator
   * @default true
   */
  showQualityIndicator?: boolean;

  /**
   * Position of the banner
   * @default 'top'
   */
  position?: 'top' | 'bottom';

  /**
   * Custom offline message
   */
  offlineMessage?: string;

  /**
   * Custom poor connection message
   */
  poorConnectionMessage?: string;

  /**
   * Callback when retry is clicked
   */
  onRetry?: () => void;

  /**
   * Z-index value
   * @default 9999
   */
  zIndex?: number;
}

interface BannerConfig {
  show: boolean;
  message: string;
  icon: React.ElementType;
  bgColor: string;
  textColor: string;
  borderColor: string;
  severity: 'error' | 'warning' | 'info';
}

/**
 * NetworkStatusBanner - Displays network connectivity status
 *
 * Features:
 * - Real-time online/offline detection
 * - Connection quality warning (poor/fair/good/excellent)
 * - Auto-dismiss when connection restored
 * - Retry button for manual reconnection
 * - Smooth slide animations (Framer Motion)
 * - Mobile & desktop responsive
 *
 * @example
 * ```tsx
 * // In root layout
 * <NetworkStatusBanner
 *   position="top"
 *   autoHideDelay={3000}
 *   showQualityIndicator
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Custom messages
 * <NetworkStatusBanner
 *   offlineMessage="İnternet bağlantısı yok"
 *   poorConnectionMessage="Yavaş bağlantı tespit edildi"
 *   onRetry={() => window.location.reload()}
 * />
 * ```
 */
export function NetworkStatusBanner({
  showWhenOnline = false,
  autoHideDelay = 3000,
  showQualityIndicator = true,
  position = 'top',
  offlineMessage = 'No internet connection',
  poorConnectionMessage = 'Poor connection detected',
  onRetry,
  zIndex = 9999,
}: NetworkStatusBannerProps) {
  const { isOnline, connectionQuality, checkStatus } = useNetworkStatus({
    debounceMs: 500,
  });

  const [isVisible, setIsVisible] = React.useState(false);
  const [wasOffline, setWasOffline] = React.useState(false);
  const autoHideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine banner configuration based on connection status
  const getBannerConfig = React.useCallback((): BannerConfig => {
    // Offline state
    if (!isOnline) {
      return {
        show: true,
        message: offlineMessage,
        icon: LuWifiOff,
        bgColor: '#FEE2E2', // red.50
        textColor: '#991B1B', // red.800
        borderColor: '#FCA5A5', // red.300
        severity: 'error',
      };
    }

    // Poor connection warning
    if (showQualityIndicator && connectionQuality === 'poor') {
      return {
        show: true,
        message: poorConnectionMessage,
        icon: LuTriangleAlert,
        bgColor: '#FEF3C7', // yellow.50
        textColor: '#92400E', // yellow.800
        borderColor: '#FDE68A', // yellow.300
        severity: 'warning',
      };
    }

    // Fair connection info (optional)
    if (showQualityIndicator && connectionQuality === 'fair') {
      return {
        show: showWhenOnline,
        message: 'Connection quality: Fair',
        icon: LuWifi,
        bgColor: '#DBEAFE', // blue.50
        textColor: '#1E40AF', // blue.800
        borderColor: '#BFDBFE', // blue.300
        severity: 'info',
      };
    }

    // Good/Excellent connection (don't show unless forced)
    return {
      show: showWhenOnline,
      message: `Connection quality: ${connectionQuality}`,
      icon: LuWifi,
      bgColor: '#D1FAE5', // green.50
      textColor: '#065F46', // green.800
      borderColor: '#A7F3D0', // green.300
      severity: 'info',
    };
  }, [
    isOnline,
    connectionQuality,
    showQualityIndicator,
    showWhenOnline,
    offlineMessage,
    poorConnectionMessage,
  ]);

  const bannerConfig = getBannerConfig();

  // Handle visibility logic
  React.useEffect(() => {
    // Clear any existing timer
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }

    // Show banner if needed
    if (bannerConfig.show) {
      setIsVisible(true);
      setWasOffline(!isOnline);
    } else {
      // Auto-hide after delay when connection restored
      if (wasOffline && isOnline) {
        autoHideTimerRef.current = setTimeout(() => {
          setIsVisible(false);
          setWasOffline(false);
        }, autoHideDelay);
      } else {
        setIsVisible(false);
      }
    }

    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, [bannerConfig.show, isOnline, wasOffline, autoHideDelay]);

  // Handle retry
  const handleRetry = React.useCallback(() => {
    checkStatus();
    onRetry?.();
  }, [checkStatus, onRetry]);

  // Handle manual dismiss
  const handleDismiss = React.useCallback(() => {
    setIsVisible(false);
    setWasOffline(false);
  }, []);

  const Icon = bannerConfig.icon;

  // Animation variants
  const variants = {
    initial: position === 'top' ? { y: -100, opacity: 0 } : { y: 100, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: position === 'top' ? { y: -100, opacity: 0 } : { y: 100, opacity: 0 },
  };

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <Box
          asChild
          position="fixed"
          top={position === 'top' ? '0' : undefined}
          bottom={position === 'bottom' ? '0' : undefined}
          left="0"
          right="0"
          zIndex={zIndex}
        >
          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
            }}
          >
            <Box
              backgroundColor={bannerConfig.bgColor}
              borderBottomWidth={position === 'top' ? '1px' : '0'}
              borderTopWidth={position === 'bottom' ? '1px' : '0'}
              borderColor={bannerConfig.borderColor}
              paddingX={{ base: 4, md: 6 }}
              paddingY={3}
              boxShadow={
                position === 'top'
                  ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  : '0 -4px 6px -1px rgba(0, 0, 0, 0.1)'
              }
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                maxWidth="1280px"
                marginX="auto"
              >
                {/* Left: Icon + Message */}
                <HStack gap={3} flex={1}>
                  <Icon size={20} color={bannerConfig.textColor} />
                  <Text fontSize="sm" fontWeight="medium" color={bannerConfig.textColor}>
                    {bannerConfig.message}
                  </Text>
                </HStack>

                {/* Right: Actions */}
                <HStack gap={2}>
                  {/* Retry button (offline only) */}
                  {!isOnline && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="red"
                      leftIcon={<LuRefreshCw />}
                      onClick={handleRetry}
                    >
                      Retry
                    </Button>
                  )}

                  {/* Dismiss button */}
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Dismiss"
                    onClick={handleDismiss}
                    icon={
                      <Box fontSize="lg" fontWeight="bold" color={bannerConfig.textColor}>
                        ×
                      </Box>
                    }
                  />
                </HStack>
              </HStack>
            </Box>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
}
